import Joi from 'joi';
import { set } from 'lodash';
import { PostcodeValidator } from '@dpdgroupuk/consumer-shipping-helper';
import { FORM, REGEX, STRINGS } from '../constants';
import { StringUtil } from '../utils';
import {
  CITYSPRINT_HELP_FIELDS,
  SEARCH_FIELD_SET,
} from '../pages/Help/constants';
import { HELP_LABELS } from '../constants/strings';
import * as AddressHelpers from './address';
import * as Validators from './validators';

export const required = value => (value ? undefined : STRINGS.REQUIRED);

export const empty = [null, ''];

export const emptyString = Joi.string().allow(...empty);

export const phoneNumber = label =>
  Joi.string()
    .pattern(REGEX.COMMON_PHONE_NUMBER_REGEXP)
    .max(15)
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        STRINGS.PHONE_NUMBER
      ),
    })
    .label(label || FORM.PHONE_NUMBER);

export const strictRequired = Joi.required().empty(...empty);

export const isPostcodeValid = (postcode, countryValue) =>
  PostcodeValidator.isPostcodeValid(postcode, countryValue);

export const validatePostcode = ({ value, helpers, countryValue }) => {
  const bfpoResult = bfpoValidation(value, helpers);

  // code property only exists if there is a JOI error
  if (bfpoResult && bfpoResult.code) {
    return bfpoResult;
  }
  if (!isPostcodeValid(value, countryValue)) {
    return helpers.error('postcode.country.invalid');
  }

  return value;
};

// Validator for Joi with react-final-form integration
export const validateWithJoi = (values, schema, options = {}) => {
  const { error } = schema.validate(values, {
    abortEarly: false,
    errors: { wrap: { label: false } },
    ...options,
  });

  if (error) {
    return error.details.reduce((acc, cur) => {
      set(acc, cur.path.join('.'), cur.message);

      return acc;
    }, {});
  }

  return {};
};

// NOTE: Somehow only this approach works for objects
export const requiredObject = Joi.object().required().empty(null);

const getValidationMessage = (schema, key) => {
  if (schema?._preferences?.messages) {
    return (
      schema._preferences.messages[key]?.rendered ||
      schema._preferences.messages[key]?.source ||
      null
    );
  }
  return null;
};

export const requireKeysSchema = (schema, requireKeys = []) => {
  const makeRequired = x => {
    if (x.type === 'object') {
      return x.concat(requiredObject);
    }

    const existingMessage = getValidationMessage(x, 'string.empty');

    return x
      .required()
      .empty(...empty)
      .invalid(...empty)
      .messages({
        'string.empty': existingMessage || 'Please enter {#label}', // NOTE: Needed in combination with prevention of missing objects
      });
  };

  return schema.fork(requireKeys, makeRequired);
};

export const getJoiFieldFlag = (schema, fieldName, validationFlag) => {
  // Get schema flags
  const fieldSchema = schema.extract(fieldName);

  // Check field validation flag
  return fieldSchema?._flags?.presence === validationFlag;
};

export const isFieldRequired = (schema, fieldName) =>
  getJoiFieldFlag(schema, fieldName, 'required');

export const numberOfParcels = Joi.number()
  .min(1)
  .max(99)
  .required()
  .label(FORM.QUANTITY);
const totalWeight = Joi.number().greater(0).required();
const parcelNumber = Joi.string()
  .pattern(REGEX.PARCEL_NUMBER_REGEXP)
  .concat(strictRequired)
  .empty('', null)
  .messages({
    'string.pattern.base': STRINGS.PARCEL_NUMBER_MUST_CONTAIN_14_DIGITS,
    'any.required': STRINGS.PARCEL_NUMBER_IS_REQUIRED,
  });

export const postcode = Joi.string().max(8);

export const bfpoValidation = (value, helpers) => {
  if (REGEX.BFPO_POSTCODES.some(regex => regex.test(value))) {
    return helpers.error('postcode.bfpo.invalid');
  }
  return value;
};

export const addressSchema = {
  street: Joi.string().max(35).label(FORM.ADDRESS_LINE_1),
  locality: emptyString.max(35).label(FORM.ADDRESS_LINE_2),
  town: Joi.string().max(35).label(FORM.CITY),
  county: emptyString.max(35).label(FORM.COUNTY),
  organisation: emptyString.max(35).label(FORM.COMPANY_NAME),
  countryCode: Joi.string().label(FORM.COUNTRY),
};

export const addressSchemaWithPostCode = countryValue => ({
  ...addressSchema,
  postcode: postcode
    .label(FORM.POSTAL_ZIP_CODE)
    .custom((value, helpers) =>
      validatePostcode({ value, helpers, countryValue })
    )
    .messages(STRINGS.CUSTOM_VALIDATION_ERROR_MESSAGES)
    .allow(...empty),
});

export const contactSchema = {
  contactName: Joi.string().max(35).required().label(FORM.CONTACT_NAME),
  nickname: Joi.string().max(35).required().label(FORM.NICKNAME),
  email: Joi.string()
    .pattern(REGEX.EMAIL_REGEX)
    .required()
    .max(100)
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        STRINGS.EMAIL
      ),
    })
    .label(FORM.EMAIL),
  phoneNumber: phoneNumber(FORM.PHONE_NUMBER).required(),
  vatNumber: emptyString.min(4).max(45).label(FORM.VAT_NUMBER),
  eoriNumber: emptyString.min(4).max(45).label(FORM.EORI_NUMBER),
  pidNumber: emptyString.min(4).max(45).label(FORM.PID_NUMBER),
  ukimsNumber: emptyString.length(32).label(FORM.UKIMS_NUMBER),
};

export const accountDetailsSchema = Joi.object({
  [FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.FIRST_NAME.NAME]: Joi.string()
    .max(15)
    .required()
    .label(FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.FIRST_NAME.LABEL),
  [FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.LAST_NAME.NAME]: Joi.string()
    .max(15)
    .required()
    .label(FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.LAST_NAME.LABEL),
  [FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.EMAIL_ADDRESS.NAME]: Joi.string()
    .pattern(REGEX.EMAIL_REGEX)
    .required()
    .max(100)
    .label(FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.EMAIL_ADDRESS.LABEL)
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        STRINGS.EMAIL
      ),
    }),
  [FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.PHONE_NUMBER.NAME]: Joi.string()
    .allow(null)
    .label(FORM.ACCOUNT_DETAILS_FIELDS.ACCOUNT_DETAILS.PHONE_NUMBER.LABEL),
});

export const addressBookSchema = countryValue =>
  Joi.object({
    address: Joi.object(addressSchemaWithPostCode(countryValue)),
    ...contactSchema,
  });

export const getNetworksSchema = () =>
  Joi.object({
    deliveryDetails: Joi.object({
      address: Joi.object({
        countryCode: Joi.string().required(),
        town: Joi.string().max(35).required(),
        county: emptyString.max(35),
        postcode: postcode
          .when(Joi.ref('$deliveryCountryValue.isPostcodeRequired'), {
            is: true,
            then: postcode.required(),
            otherwise: postcode.optional().allow('', null),
          })
          .label(FORM.POSTAL_ZIP_CODE)
          .custom((value, helpers) => {
            const { deliveryCountryValue } = helpers.prefs.context; // Access context
            return validatePostcode({
              value,
              helpers,
              countryValue: deliveryCountryValue,
            });
          })
          .messages(STRINGS.CUSTOM_VALIDATION_ERROR_MESSAGES),
      }).required(),
    }).required(),
    collectionDetails: Joi.object({
      address: Joi.object({
        countryCode: Joi.string().required(),
        town: Joi.string().max(35).required(),
        county: emptyString.max(35),
        postcode: postcode
          .required()
          .label(FORM.POSTAL_ZIP_CODE)
          .custom((value, helpers) => {
            const { collectionCountryValue } = helpers.prefs.context; // Access context
            return validatePostcode({
              value,
              helpers,
              countryValue: collectionCountryValue,
            });
          })
          .messages(STRINGS.CUSTOM_VALIDATION_ERROR_MESSAGES),
      }).required(),
    }).required(),
    numberOfParcels,
    totalWeight,
    isBusiness: Joi.boolean(),
    atRisk: Joi.boolean(),
  }).required();

export const voucherSchema = Joi.object({
  voucherCode: Joi.string()
    .min(3)
    .messages({
      'string.min': STRINGS.GREATER_OR_$_CHARACTERS(3),
    }),
});

export const trackParcelSchema = Joi.object({
  [FORM.FIELDS.PARCEL_NUMBER.KEY]: parcelNumber,
  [FORM.FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.KEY]: Joi.alternatives()
    .try(
      Joi.string().pattern(REGEX.ORDER_ID_REGEX),
      postcode.concat(strictRequired).empty('', null)
    )
    .required()
    .label(FORM.FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.LABEL)
    .messages({
      'alternatives.match': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        FORM.FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.LABEL
      ),
      'alternatives.types': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        FORM.FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.LABEL
      ),
    }),
});

export const contactDetailsHelpSchema = Joi.object({
  name: Joi.string()
    .max(100)
    .required()
    .label(FORM.HELP_FIELDS.CONTACT_NAME.LABEL),
  email: Joi.string()
    .pattern(REGEX.EMAIL_REGEX)
    .required()
    .max(100)
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        STRINGS.EMAIL
      ),
    })
    .label(FORM.HELP_FIELDS.EMAIL.LABEL),
  phone: phoneNumber(FORM.HELP_FIELDS.PHONE.LABEL).allow(...empty),
});

const conditionalValidation = (
  baseRules,
  { damagedRules, notArrivedRules, missingContentsRules }
) =>
  baseRules.when('$reasonForContact', {
    is: 'Damaged',
    then: damagedRules,
    otherwise: Joi.when('$reasonForContact', {
      is: 'Not Arrived',
      then: notArrivedRules,
      otherwise: missingContentsRules,
    }),
  });

export const helpParcelSchema = Joi.object({
  [FORM.HELP_FIELDS.PARCEL_NUMBER.KEY]: parcelNumber.when(
    Joi.ref('$activeSearchFieldSet'),
    {
      is: SEARCH_FIELD_SET.DELIVERY,
      then: Joi.required(),
      otherwise: Joi.optional().allow('', null),
    }
  ),
  [FORM.HELP_FIELDS.POSTCODE_OR_ORDER_ID.KEY]: Joi.alternatives()
    .try(
      Joi.string().pattern(REGEX.ORDER_ID_REGEX),
      postcode.concat(strictRequired).empty('', null)
    )
    .when(Joi.ref('$activeSearchFieldSet'), {
      is: SEARCH_FIELD_SET.DELIVERY,
      then: Joi.required(),
      otherwise: Joi.optional().allow('', null),
    })
    .label(FORM.FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.LABEL)
    .messages({
      'alternatives.match': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        FORM.FIELDS.POSTCODE_OR_ORDER_ID_SEARCH.LABEL
      ),
    }),
  [FORM.HELP_FIELDS.COLLECTION_REFERENCE.KEY]: Joi.string()
    .pattern(REGEX.DIGIT_11)
    .when(Joi.ref('$activeSearchFieldSet'), {
      is: SEARCH_FIELD_SET.COLLECTION,
      then: Joi.required(),
      otherwise: Joi.optional().allow('', null),
    })
    .messages({
      'any.required':
        STRINGS.HELP_PAGE_VALIDATION_MESSAGES.COLLECTION_REFERENCE_IS_REQUIRED,
      'string.empty':
        STRINGS.HELP_PAGE_VALIDATION_MESSAGES.COLLECTION_REFERENCE_IS_REQUIRED,
      'string.pattern.base':
        STRINGS.HELP_PAGE_VALIDATION_MESSAGES.COLLECTION_REFERENCE_INVALID,
    })
    .label(FORM.HELP_FIELDS.COLLECTION_POSTCODE.LABEL),
  [FORM.HELP_FIELDS.COLLECTION_POSTCODE.KEY]: postcode
    .concat(strictRequired)
    .empty('', null)
    .when(Joi.ref('$activeSearchFieldSet'), {
      is: SEARCH_FIELD_SET.COLLECTION,
      then: Joi.required(),
      otherwise: Joi.optional().allow('', null),
    })
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        FORM.HELP_FIELDS.COLLECTION_POSTCODE.LABEL
      ),
    })
    .label(FORM.HELP_FIELDS.COLLECTION_POSTCODE.LABEL),
  [FORM.HELP_FIELDS.ISSUE_TYPE.KEY]: Joi.string().required(),
  [FORM.HELP_FIELDS.HELP_TYPE.KEY]: Joi.string().required(),
  [FORM.HELP_KEYS.CONTACT_DETAILS]: contactDetailsHelpSchema,
  [FORM.HELP_KEYS.PARCEL_CONTENTS]: Joi.string()
    .max(255)
    .required()
    .messages({
      'any.required': STRINGS.HELP_PAGE_VALIDATION_MESSAGES.PARCEL_CONTENTS,
      'string.empty': STRINGS.HELP_PAGE_VALIDATION_MESSAGES.PARCEL_CONTENTS,
    })
    .label(HELP_LABELS.PARCEL_CONTENTS),
  [FORM.HELP_KEYS.GOODS_VALUE]: conditionalValidation(
    Joi.string().custom((value, helpers) => {
      const match = value.match(/^(\D?)(\d+(\.\d+)?)$/);
      if (match && parseFloat(match[2], 10) > 1000) {
        return helpers.error('number.max', { limit: 1000 });
      }
      return value;
    }),
    {
      damagedRules: Joi.required()
        .messages({
          'any.required': STRINGS.HELP_PAGE_VALIDATION_MESSAGES.VALUE_DAMAGED,
          'string.empty': STRINGS.HELP_PAGE_VALIDATION_MESSAGES.VALUE_DAMAGED,
          'number.max':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.VALUE_GREATER_THAN,
        })
        .label(HELP_LABELS.DAMAGED_ITEM_VALUE),
      notArrivedRules: Joi.required()
        .messages({
          'any.required':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.VALUE_NOT_ARRIVED,
          'string.empty':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.VALUE_NOT_ARRIVED,
          'number.max':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.VALUE_GREATER_THAN,
        })
        .label(HELP_LABELS.PARCEL_VALUE),
      missingContentsRules: Joi.allow(...empty)
        .label(HELP_LABELS.MISSING_ITEM_VALUE)
        .messages({
          'number.max':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.VALUE_GREATER_THAN,
        }),
    }
  ),
  [FORM.HELP_KEYS.PACKAGE_DESCRIPTION]: conditionalValidation(
    Joi.string().max(1000).label(HELP_LABELS.PACKAGE_DESCRIPTION),
    {
      damagedRules: Joi.allow(...empty),
      notArrivedRules: Joi.required().messages({
        'any.required':
          STRINGS.HELP_PAGE_VALIDATION_MESSAGES.DESCRIPTION_OF_PACKAGING,
        'string.empty':
          STRINGS.HELP_PAGE_VALIDATION_MESSAGES.DESCRIPTION_OF_PACKAGING,
      }),
      missingContentsRules: Joi.allow(...empty),
    }
  ),
  [FORM.HELP_KEYS.ADDITIONAL_INFORMATION]: conditionalValidation(
    Joi.string().max(1000).label(HELP_LABELS.ADDITIONAL_INFORMATION),
    {
      damagedRules: Joi.allow(...empty),
      notArrivedRules: Joi.required().messages({
        'any.required':
          STRINGS.HELP_PAGE_VALIDATION_MESSAGES.ADDITIONAL_INFORMATION,
        'string.empty':
          STRINGS.HELP_PAGE_VALIDATION_MESSAGES.ADDITIONAL_INFORMATION,
      }),
      missingContentsRules: Joi.allow(...empty),
    }
  ),
  [FORM.HELP_KEYS.DAMAGE_DESCRIPTION]: conditionalValidation(
    Joi.string().max(255).required(),
    {
      damagedRules: Joi.required()
        .messages({
          'any.required':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.DAMAGED_DESCRIPTION,
          'string.empty':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.DAMAGED_DESCRIPTION,
        })
        .label(HELP_LABELS.DAMAGE_DETAIL),
      notArrivedRules: Joi.forbidden(),
      missingContentsRules: Joi.string()
        .required()
        .messages({
          'any.required':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.PACKAGING_CONDITION,
          'string.empty':
            STRINGS.HELP_PAGE_VALIDATION_MESSAGES.PACKAGING_CONDITION,
        })
        .label(HELP_LABELS.PACKAGE_DAMAGE),
    }
  ),
  [FORM.HELP_KEYS.MISSING_DESCRIPTION]: conditionalValidation(
    Joi.string().max(255).label(HELP_LABELS.WHAT_MISSING),
    {
      damagedRules: Joi.forbidden(),
      notArrivedRules: Joi.forbidden(),
      missingContentsRules: Joi.required().messages({
        'any.required':
          STRINGS.HELP_PAGE_VALIDATION_MESSAGES.MISSING_FROM_PARCEL,
        'string.empty':
          STRINGS.HELP_PAGE_VALIDATION_MESSAGES.MISSING_FROM_PARCEL,
      }),
    }
  ),
  [FORM.HELP_KEYS.DAMAGED_ITEM]: Joi.string()
    .max(255)
    .label(HELP_LABELS.WHAT_DAMAGED)
    .messages({
      'any.required': STRINGS.HELP_PAGE_VALIDATION_MESSAGES.DAMAGED_ITEMS,
      'string.empty': STRINGS.HELP_PAGE_VALIDATION_MESSAGES.DAMAGED_ITEMS,
    })
    .when('$reasonForContact', {
      is: 'Damaged',
      then: Joi.required(),
      otherwise: Joi.forbidden(),
    }),
  [FORM.HELP_KEYS.ATTACHED_IMAGES]: Joi.array()
    .allow('', null)
    .items(
      Joi.object({
        image: Joi.string(),
        fileName: Joi.string(),
        contentType: Joi.string(),
        size: Joi.number(),
        preview: Joi.string(),
      })
    ),
});

export const citysprintHelpSupportSchema = Joi.object({
  [CITYSPRINT_HELP_FIELDS.BOOKING_REFERENCE.KEY]: Joi.string()
    .trim()
    .pattern(/^CS/i)
    .min(5)
    .required()
    .messages({
      'string.pattern.base': STRINGS.HELP_SAMEDAY_BOOKING_REFERENCE_INVALID,
      'string.min': STRINGS.HELP_SAMEDAY_BOOKING_REFERENCE_INVALID,
      'any.required': STRINGS.HELP_SAMEDAY_BOOKING_REFERENCE_REQUIRED,
      'string.empty': STRINGS.HELP_SAMEDAY_BOOKING_REFERENCE_REQUIRED,
    })
    .label(CITYSPRINT_HELP_FIELDS.BOOKING_REFERENCE.LABEL),
  [CITYSPRINT_HELP_FIELDS.DELIVERY_POSTCODE.KEY]: postcode
    .concat(strictRequired)
    .pattern(REGEX.UK_POST_CODE_FULL)
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        'UK postcode'
      ),
      'any.required': STRINGS.HELP_SAMEDAY_DELIVERY_POSTCODE_REQUIRED,
      'string.empty': STRINGS.HELP_SAMEDAY_DELIVERY_POSTCODE_REQUIRED,
    })
    .label(CITYSPRINT_HELP_FIELDS.DELIVERY_POSTCODE.LABEL),
});

export const isCitysprintHelpSearchValid = values => {
  const { error } = citysprintHelpSupportSchema.validate(values, {
    abortEarly: true,
  });

  return !error;
};

export const loginEmailSchema = Joi.object({
  [FORM.FIELDS.EMAIL.KEY]: Joi.string()
    .pattern(REGEX.EMAIL_REGEX)
    .concat(strictRequired)
    .max(100)
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        STRINGS.EMAIL
      ),
      'any.required': STRINGS.REQUIRED,
      'string.max': STRINGS.LESS_OR_$_CHARACTERS(100),
    }),
});

export const isUKPostcodeValid = postcode =>
  REGEX.UK_POST_CODE_FULL.test(postcode);

export const validateNetworkQuery = ({
  countries,
  deliveryCountryCode,
  collectionCountryCode,
  networksQuery,
}) => {
  const deliveryCountryValue = AddressHelpers.getCountryValue({
    countries,
    countryCode: deliveryCountryCode,
  });
  const collectionCountryValue = AddressHelpers.getCountryValue({
    countries,
    countryCode: collectionCountryCode,
  });

  const { error } = Validators.getNetworksSchema().validate(networksQuery, {
    context: {
      deliveryCountryValue,
      collectionCountryValue,
    },
  });
  return error;
};

export const shareParcelSchema = Joi.object({
  [FORM.FIELDS.EMAIL.KEY]: Joi.string()
    .pattern(REGEX.EMAIL_REGEX)
    .concat(strictRequired)
    .max(100)
    .messages({
      'string.pattern.base': StringUtil.formatMessage(
        STRINGS.PLEASE_ENTER_VALID_$,
        STRINGS.EMAIL
      ),
      'any.required': STRINGS.REQUIRED,
      'string.max': STRINGS.LESS_OR_$_CHARACTERS(100),
    }),
});
